import { useCallback, useState } from "react";
import { useIsFreeMember } from "./useMembership";
import { downloadFileWithFallback } from "../Utils/DownloadUtils";
import { getPayrollErrorCode } from "../Utils/payroll";

/**
 * usePayrollDownload - downloads a payslip PDF the payrollRun Lambda prints
 * into the caller's private storage.
 *
 * PDFs are for paying members. A free member is shown the premium prompt
 * before anything is requested; the server checks again, and its
 * PREMIUM_REQUIRED opens the same prompt, so a lapsed plan the client has not
 * noticed yet ends the same way.
 *
 * `download(id, request)` - `id` names what is downloading, so one
 * button of many can show it; `request` resolves to { key, fileName }.
 */
export const usePayrollDownload = () => {
  const isFree = useIsFreeMember();
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);
  const [premiumOpen, setPremiumOpen] = useState(false);

  const download = useCallback(
    async (id, request) => {
      setError(null);
      if (isFree) {
        setPremiumOpen(true);
        return false;
      }
      setDownloading(id);
      try {
        const { key, fileName } = await request();
        await downloadFileWithFallback(key, undefined, () => fileName);
        return true;
      } catch (err) {
        const code = getPayrollErrorCode(err);
        if (code === "PREMIUM_REQUIRED") setPremiumOpen(true);
        else setError(err);
        return false;
      } finally {
        setDownloading(null);
      }
    },
    [isFree],
  );

  const clearError = useCallback(() => setError(null), []);

  return { download, downloading, error, clearError, isFree, premiumOpen, setPremiumOpen };
};
